import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  Box,
  Typography,
  Paper,
  Chip,
  useTheme,
  Avatar,
  Grid,
  Button,
  useMediaQuery,
  styled,
  IconButton
} from '@mui/material';
import {
  WorkHistory,
  OpenInNew,
  Code,
  Group,
  CalendarMonth,
  LocationOn,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import React from 'react';

const ExperienceCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '20px',
  height: '100%',
  transition: 'box-shadow 0.2s, border-color 0.2s',
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    boxShadow: '0 6px 24px rgba(21,101,192,0.12)',
    borderColor: '#2196f3'
  }
}));

const experiences = [
  {
    company: "CIBC",
    role: "Development Lead | Frontend Architecture & Delivery",
    duration: "05/2021 - Present",
    location: "Whitby, Canada",
    highlights: [
      "Architected scalable, component-driven frontend systems for millions of users",
      "Established architectural patterns ensuring performance and maintainability",
      "Led API integration strategy with RESTful patterns and data flow architecture",
      "Drove enterprise design system standards across 50+ engineers and 15+ teams",
      "Achieved WCAG 2.1 AA compliance through collaborative design-engineering",
      "Optimized performance: reduced load times 40%+ with caching and code-splitting",
      "Mentored 8+ engineers in modern JavaScript, React, and system design",
      "Championed TDD: achieved 85%+ test coverage, reduced production bugs 60%"
    ],
    link: "https://cibc.com"
  },
  {
    company: "SCIKEY",
    role: "Sr. Software Developer | Full-Stack SaaS Architecture",
    duration: "07/2016 - 03/2021",
    location: "India",
    highlights: [
      "Architected B2B SaaS applications with Angular, Node.js, and RxJS",
      "Designed modular component architecture for complex, data-driven UIs",
      "Built robust Node.js/Express.js backends with RESTful API design",
      "Implemented real-time features with MongoDB integration",
      "Optimized end-to-end performance through database indexing and API optimization",
      "Mentored junior developers in TypeScript, testing, and best practices",
      "Designed scalable architecture patterns for distributed systems"
    ],
    link: "https://scikey.ai"
  },
  {
    company: "AI & Generative AI Projects",
    role: "Personal Development & Exploration",
    duration: "Ongoing",
    location: "Remote",
    highlights: [
      "Designed RAG API using ChromaDB for intelligent document retrieval",
      "Built AI-powered legal case database with Ollama + ChromaDB semantic search",
      "Created automated code review workflow leveraging Google Gemini API",
      "Developed with Claude API, Google Gemini, and GPT-5 across use cases",
      "Explored model selection strategies and prompt engineering best practices",
      "Integrated agentic solutions into development workflows"
    ],
    link: ""
  }
];

const Experience = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % experiences.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + experiences.length) % experiences.length);
  };

  const currentExp = experiences[currentIndex];

  const cardVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5 }
    },
    exit: { opacity: 0, x: -20, transition: { duration: 0.3 } }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Typography className="minimal-title gradient-text" variant="h2" sx={{
        textAlign: 'center',
        mb: 6,
        fontWeight: 700,
        fontSize: isMobile ? '2.5rem' : '3rem'
      }}>
        Work Experience
      </Typography>

      <Box
        ref={ref}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'center'
        }}
      >
        <IconButton
          onClick={handlePrev}
          sx={{
            color: theme.palette.primary.main,
            '&:hover': {
              bgcolor: 'rgba(33, 150, 243, 0.1)'
            }
          }}
          disabled={experiences.length <= 1}
        >
          <ChevronLeft />
        </IconButton>

        <Box sx={{ flex: 1, maxWidth: 700 }}>
          <motion.div
            key={currentIndex}
            variants={cardVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            exit="exit"
          >
            <ExperienceCard elevation={0}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 3,
                  flexDirection: isMobile ? 'column' : 'row'
                }}>
                  <Avatar sx={{
                    bgcolor: theme.palette.primary.main,
                    width: 60,
                    height: 60,
                    color: theme.palette.getContrastText(theme.palette.primary.main)
                  }}>
                    <WorkHistory fontSize="medium" />
                  </Avatar>
                  <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {currentExp.company}
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                      {currentExp.role}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 3,
                  flexWrap: 'wrap',
                  justifyContent: isMobile ? 'center' : 'flex-start'
                }}>
                  <Chip
                    icon={<CalendarMonth />}
                    label={currentExp.duration}
                    size="small"
                    sx={{
                      bgcolor: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.1)'
                        : 'rgba(0,0,0,0.05)'
                    }}
                  />
                  <Chip
                    icon={<LocationOn />}
                    label={currentExp.location}
                    size="small"
                    sx={{
                      bgcolor: theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.1)'
                        : 'rgba(0,0,0,0.05)'
                    }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <ul style={{
                    paddingLeft: 24,
                    margin: 0,
                    listStyleType: 'none'
                  }}>
                    {currentExp.highlights.map((highlight, i) => (
                      <li key={highlight} style={{ marginBottom: '10px' }}>
                        <Typography variant="body2" sx={{
                          position: 'relative',
                          lineHeight: 1.5,
                          '&:before': {
                            content: '"▸"',
                            position: 'absolute',
                            left: -20,
                            color: theme.palette.primary.main,
                            fontWeight: 'bold'
                          }
                        }}>
                          {highlight}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </Box>
            </ExperienceCard>
          </motion.div>
        </Box>

        <IconButton
          onClick={handleNext}
          sx={{
            color: theme.palette.primary.main,
            '&:hover': {
              bgcolor: 'rgba(33, 150, 243, 0.1)'
            }
          }}
          disabled={experiences.length <= 1}
        >
          <ChevronRight />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 4 }}>
        {experiences.map((_, index) => (
          <Box
            key={index}
            onClick={() => setCurrentIndex(index)}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: currentIndex === index ? theme.palette.primary.main : 'rgba(0,0,0,0.2)',
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                bgcolor: theme.palette.primary.main
              }
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default Experience;